export interface GuideSection { heading: string; body?: string; bullets?: string[] }
export interface GuideFaq { q: string; a: string }
export interface Guide {
  path: string;
  title: string; // <title>
  description: string; // meta description
  h1: string;
  question: string;
  intro: string;
  sections: GuideSection[];
  faqs: GuideFaq[];
}

export const guides: Guide[] = [
  {
    path: "/about-hpe",
    title: "What Is Hair Pro Encyclopedia.ai? | In-Chair Hair Intelligence",
    description: "Hair Pro Encyclopedia.ai is an AI hair assistant for stylists, barbers, and cosmetology students, founded by a cosmetologist with 35+ years of experience.",
    h1: "What Is Hair Pro Encyclopedia.ai?",
    question: "What is Hair Pro Encyclopedia.ai?",
    intro:
      "Hair Pro Encyclopedia.ai is In-Chair Hair Intelligence: an AI assistant that gives hair professionals instant answers behind the chair. Stylists describe what they see, add photos if they want, and get a structured, step-by-step plan for color, cutting, and correction — written for licensed professionals, not for school theory.",
    sections: [
      {
        heading: "Who it serves",
        bullets: [
          "Licensed cosmetologists and barbers working behind the chair",
          "Cosmetology and barber students with an approved credential",
          "Salon professionals who want a calm second opinion before a chemical service",
        ],
      },
      {
        heading: "How it supports your decisions",
        body:
          "Every answer follows the same scan-friendly order: Formula, Application, Watch points, Why it works, and Warnings. You can keep talking the case through with HPE on the same answer page, so you never have to re-explain the client. HPE supports your judgment — it never replaces your consultation, a strand test, or the manufacturer's instructions.",
      },
      {
        heading: "Founded by a working cosmetologist",
        body:
          "Hair Pro Encyclopedia.ai was founded by a cosmetologist with more than 35 years of experience behind the chair. The guidance reflects how real stylists think through a service: assess first, correct calmly, and protect the hair.",
      },
      {
        heading: "Brand-neutral by design",
        body:
          "HPE explains the logic in universal color and cutting terms first, so the plan works with the professional line you already use. Brand examples, when shown, are examples — always confirm on your product's instructions.",
      },
    ],
    faqs: [
      { q: "Is Hair Pro Encyclopedia.ai free?", a: "Every new visitor can ask one in-chair question free. Continued access requires a membership after a quick professional verification." },
      { q: "Does HPE replace manufacturer instructions?", a: "No. HPE is educational AI assistance. Manufacturer directions, your consultation, and your professional judgment always come first." },
      { q: "Can HPE make mistakes?", a: "Yes. Like any AI, it can be wrong. Verify formulas and chemical services, and strand test when in doubt." },
    ],
  },
  {
    path: "/ai-hair-help",
    title: "AI Hair Help Behind the Chair | Hair Pro Encyclopedia.ai",
    description: "How hairstylists use AI behind the chair: what to include in your question, how Ask HPE responds, and how to weigh guidance against your consultation.",
    h1: "AI Hair Help Behind the Chair",
    question: "How can hairstylists use AI for help behind the chair?",
    intro:
      "Hairstylists use AI behind the chair to get a fast, organized second opinion while the client is still seated. With Ask HPE, you describe the current hair, the goal, and the history, add photos if helpful, and receive a step-by-step plan you can check against your own consultation and your product's instructions.",
    sections: [
      {
        heading: "How Ask HPE works",
        bullets: [
          "Optionally start with PRIME Your CANVAS™ — a short guided intake — or skip it and type your question",
          "Upload photos of the hair if you have them",
          "Get guidance in order: Formula, Application, Watch, Why, Warning",
          "Keep talking it through on the same answer — follow-up questions stay attached to that case",
        ],
      },
      {
        heading: "What makes a question useful",
        bullets: [
          "Current level and tone, at the root, mid-lengths, and ends",
          "The desired result, in plain words or a reference description",
          "Chemical history: box dye, henna, lightener, relaxers, keratin",
          "Condition: porosity, elasticity, breakage, or banding you can see",
          "What you have on hand — line, developers, bond builder",
        ],
      },
      {
        heading: "How professionals evaluate AI guidance",
        body:
          "Treat every answer as a plan to test, not a guarantee. Compare it against your consultation findings, confirm mixing and timing on the manufacturer's directions, and perform a strand test whenever history is uncertain — especially if box dye or metallic salts are possible.",
      },
    ],
    faqs: [
      { q: "Do I need to complete PRIME Your CANVAS™?", a: "No. It's optional. You can go straight to photos or your question." },
      { q: "Do follow-up questions use up my free question?", a: "No. Clarifications about the same client case stay in the same conversation. A new client or unrelated issue counts as a new question." },
    ],
  },
  {
    path: "/hair-color-formula-guidance",
    title: "What You Need to Plan a Hair Color Formula | HPE",
    description: "The information a professional needs before formulating hair color: starting level, underlying pigment, target, gray percentage, history, porosity, and product rules.",
    h1: "Understanding Hair Color Formulas",
    question: "What information is needed to plan a professional hair color formula?",
    intro:
      "To plan a professional hair color formula, you need the starting level, the underlying pigment that will be exposed, the target level and tone, the gray percentage, the hair's chemical history, its condition and porosity, and the specific instructions for the color line you're using. There is no single universal formula — the right one comes from these facts together.",
    sections: [
      { heading: "Starting level", body: "Read the natural level at the root and any artificial color through the lengths separately. They often differ and may need different formulas." },
      { heading: "Underlying pigment", body: "As hair lightens, warm pigment is exposed. Knowing what warmth will appear at your target tells you which tone you need to neutralize or enhance." },
      { heading: "Target result", body: "Define the goal level and tone clearly. Decide whether you need lift, deposit only, or both — that drives your color type and developer choice." },
      { heading: "Gray percentage", body: "Estimate gray in each zone. Resistant or high-percentage gray may need a different approach than the rest of the head, per the manufacturer's guidance." },
      { heading: "Hair history", body: "Ask directly about box dye, henna, and at-home products. Previous artificial color does not lift like natural pigment, and unknown history is a reason to strand test before any chemical service." },
      { heading: "Condition and porosity", body: "Porous ends can grab color darker or fade faster. Compromised hair changes what's safe — sometimes the best plan is a gentler service in more than one visit." },
      { heading: "Product-specific instructions", body: "Mixing ratios, developer strengths, and timing vary by line. Always follow the manufacturer's directions for the products in your hand." },
    ],
    faqs: [
      { q: "Will HPE give me an exact formula?", a: "HPE gives a formula plan with the reasoning behind it and brand examples where relevant. Always confirm ratios and timing on your color line's instructions." },
      { q: "Should I relighten hair that turned orange?", a: "Not automatically. First assess history, condition, previous processing, and the target. Sometimes toning, a bond-building step, or a second visit is the safer path." },
    ],
  },
  {
    path: "/haircutting-consultation-help",
    title: "Plan a Haircut Around Hair and Goals | HPE",
    description: "How hairstylists plan a haircut around texture, density, growth patterns, face shape, maintenance, and consultation — with help from Ask HPE.",
    h1: "Haircutting and Consultation Support",
    question: "How can hairstylists plan a haircut around a client's hair and goals?",
    intro:
      "Hairstylists plan a haircut by matching the client's goal to what their hair will actually do: texture, density, growth patterns, face shape, and how much styling time they'll give it each day. Ask HPE helps you think through head sections, elevation, and connection so the shape you plan is the shape that grows out well.",
    sections: [
      {
        heading: "What to assess before you cut",
        bullets: [
          "Texture and curl pattern — how the hair will shrink or spring when dry",
          "Density — how much weight to keep or remove",
          "Growth patterns — cowlicks, whorls, and nape direction",
          "Face shape and features you want to frame",
          "Maintenance — styling time, tools, and how often they'll return",
        ],
      },
      {
        heading: "How Ask HPE helps",
        body:
          "Describe the current cut, the goal, and the hair, and HPE responds with a cutting plan built on head planes, elevation, and dot-to-dot connection, plus watch points where shapes commonly break down. You can follow up on the same answer if something changes mid-service.",
      },
      {
        heading: "Consultation language",
        body:
          "HPE can also suggest calm, honest wording for setting expectations with the client — without promising a result their hair can't deliver.",
      },
    ],
    faqs: [
      { q: "Can HPE help with classic shapes like a wedge?", a: "Yes. Ask about a named shape and HPE will outline the elevation and sectioning logic behind it." },
      { q: "Can I upload a photo of the client's hair?", a: "Yes. Photos help HPE read texture, length, and the current shape." },
    ],
  },
  {
    path: "/professional-hair-education",
    title: "HPE for Cosmetology Students & Licensed Pros",
    description: "Who can join Hair Pro Encyclopedia.ai: licensed cosmetologists, barbers, and students with an approved credential. How verification works before membership.",
    h1: "HPE for Cosmetology Students and Licensed Professionals",
    question: "Who can use Hair Pro Encyclopedia.ai, and how does verification work?",
    intro:
      "Hair Pro Encyclopedia.ai is built for licensed cosmetologists and barbers, and for cosmetology or barber students who provide an alternative credential such as proof of school enrollment. Anyone can ask one free question first; membership opens after a professional verification is approved.",
    sections: [
      {
        heading: "How verification works",
        bullets: [
          "Sign in and open the verification step",
          "Enter your name, issuing state or country, and license number",
          "Upload a clear photo or screenshot of your cosmetology or barber license",
          "Cover your home address and other unneeded details before uploading",
          "Students and others can choose “I have another credential” and describe it",
        ],
      },
      {
        heading: "Review",
        body:
          "An uploaded image alone isn't treated as proof. Licenses are checked against an official lookup where one is supported; everything else, including student credentials, is reviewed personally. Once approved, you can choose a membership plan — and you won't need to upload again.",
      },
      {
        heading: "Your privacy",
        body: "Credential images are stored privately and are visible only to authorized reviewers.",
      },
      {
        heading: "Learning inside HPE",
        body: "Beyond Ask HPE, you can explore PRIME Your CANVAS™ and the certification quizzes to sharpen your diagnostic thinking.",
      },
    ],
    faqs: [
      { q: "Can I try HPE before verifying?", a: "Yes. Your first in-chair question is free, no verification needed." },
      { q: "Does HPE offer continuing-education credit?", a: "HPE does not currently advertise continuing-education credit or accreditation." },
    ],
  },
];
